import React, { useEffect, useRef, useState } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

// Full-size, swipeable screenshot lightbox.
export default function Lightbox({ images, index, onClose, onNavigate }) {
  const touchStart = useRef(null);
  const [i, setI] = useState(index);

  useEffect(() => setI(index), [index]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  const go = (dir) => {
    setI((prev) => {
      const next = (prev + dir + images.length) % images.length;
      onNavigate && onNavigate(next);
      return next;
    });
  };

  const onTouchStart = (e) => (touchStart.current = e.touches[0].clientX);
  const onTouchEnd = (e) => {
    if (touchStart.current == null) return;
    const dx = e.changedTouches[0].clientX - touchStart.current;
    if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
    touchStart.current = null;
  };

  if (index == null) return null;

  return (
    <div
      className="fixed inset-0 z-[95] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <button className="absolute top-6 right-6 text-white/80 hover:text-white z-10" onClick={onClose} aria-label="Close">
        <X className="w-8 h-8" />
      </button>

      <button
        className="absolute left-4 sm:left-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        onClick={(e) => { e.stopPropagation(); go(-1); }}
        aria-label="Previous"
      >
        <ChevronLeft className="w-7 h-7" />
      </button>

      <div className="max-w-5xl w-full" onClick={(e) => e.stopPropagation()}>
        <img
          src={images[i]}
          alt={`screenshot ${i + 1}`}
          className="w-full max-h-[80vh] object-contain rounded-xl shadow-2xl"
        />
        <div className="flex items-center justify-center gap-2 mt-4">
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={(e) => { e.stopPropagation(); setI(idx); }}
              className={`h-2 rounded-full transition-all ${idx === i ? "w-6 bg-cyan-400" : "w-2 bg-white/30 hover:bg-white/50"}`}
              aria-label={`Go to screenshot ${idx + 1}`}
            />
          ))}
        </div>
      </div>

      <button
        className="absolute right-4 sm:right-8 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
        onClick={(e) => { e.stopPropagation(); go(1); }}
        aria-label="Next"
      >
        <ChevronRight className="w-7 h-7" />
      </button>
    </div>
  );
}
